import { Link, NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import { MorphingShapeBackground, type MorphingShapeBlob } from "@/components/wensity/morphing-shape-background";
import { Button } from "@/components/wensity/button";
import { Separator } from "@/components/wensity/separator";
import { useSiteContent } from "@/data/SiteContentContext";
import {
  DEFAULT_BACKGROUND,
  DEFAULT_REMOTE_SUPPORT_LABEL,
  DEFAULT_REMOTE_SUPPORT_URL,
  type BackgroundConfig,
} from "@/data/site";
import { BrandMark } from "@/components/BrandMark";
import { cn } from "@/lib/utils";

function toMorphingBlobs(background: BackgroundConfig): MorphingShapeBlob[] {
  return background.blobs.map((blob) => ({
    color: `linear-gradient(135deg, ${blob.colorStart} 0%, ${blob.colorEnd} 100%)`,
    size: blob.size,
    top: blob.top,
    left: blob.left,
    blur: blob.blur,
    opacity: blob.opacity,
    duration: blob.duration,
    delay: blob.delay,
    reverse: blob.reverse,
  }));
}

export function SiteLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { site, navItems } = useSiteContent();
  const background = site.background ?? DEFAULT_BACKGROUND;

  return (
    <div className="relative min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10" style={{ opacity: background.containerOpacity }}>
        <MorphingShapeBackground
          className="h-full w-full"
          blobs={toMorphingBlobs(background)}
          grain={background.grain}
        />
      </div>

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[var(--color-surface)] focus:px-4 focus:py-2"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-[var(--color-border)] bg-[rgba(13,15,14,0.82)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="group flex items-center gap-3" aria-label={`${site.name} home`}>
            <BrandMark className="text-[var(--color-primary)] transition-transform group-hover:scale-105" />
            <span className="font-semibold tracking-tight">{site.name}</span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-3 py-2 text-sm text-[var(--color-muted)] transition hover:text-[var(--color-foreground)]",
                    "relative after:absolute after:bottom-1 after:left-3 after:h-px after:w-0 after:bg-[var(--color-primary)] after:transition-all hover:after:w-[calc(100%-1.5rem)]",
                    isActive && "text-[var(--color-foreground)] after:w-[calc(100%-1.5rem)]",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="border-[var(--color-border)] bg-transparent text-[var(--color-foreground)] md:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <div className="flex flex-col gap-1.5">
              <span className={cn("h-0.5 w-5 bg-current transition", menuOpen && "translate-y-2 rotate-45")} />
              <span className={cn("h-0.5 w-5 bg-current transition", menuOpen && "opacity-0")} />
              <span className={cn("h-0.5 w-5 bg-current transition", menuOpen && "-translate-y-2 -rotate-45")} />
            </div>
          </Button>
        </div>

        {menuOpen ? (
          <nav
            id="mobile-nav"
            className="border-t border-[var(--color-border)] bg-[rgba(13,15,14,0.96)] px-4 py-4 md:hidden"
            aria-label="Primary mobile"
          >
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "rounded-lg px-3 py-3 text-lg font-medium",
                      isActive ? "bg-white/5 text-[var(--color-primary)]" : "text-[var(--color-foreground)]",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        ) : null}
      </header>

      <main id="main" className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--color-border)] py-10">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 text-sm text-[var(--color-muted)] sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>
            © {new Date().getFullYear()} {site.author.name} · {site.author.location}
          </p>
          <div className="flex items-center gap-3">
            <a
              href={site.remoteSupportUrl || DEFAULT_REMOTE_SUPPORT_URL}
              className="hover:text-[var(--color-foreground)]"
              target="_blank"
              rel="noopener noreferrer"
            >
              {site.remoteSupportLabel || DEFAULT_REMOTE_SUPPORT_LABEL}
            </a>
            <Separator orientation="vertical" className="h-4" />
            <a href="/feed.xml" className="hover:text-[var(--color-foreground)]">
              RSS
            </a>
            <Separator orientation="vertical" className="h-4" />
            <Link to="/admin" className="hover:text-[var(--color-foreground)]">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
